import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  emberApi,
  type EmberMedia,
  type ResultResponse,
} from '../api';
import { useSession } from '../../auth/SessionProvider';
import { LIVE_EMBER_EVENT, subscribeConversation } from '../live';
import { trackFailed } from '../../../../../shared-web/track';
import { plural } from '../format';
import { navigate, replaceUrl, returnToLabel, sanitizeReturnTo, withReturnTo } from '../router';
import { useTalkSheet } from '../talk-sheet';
import { Link } from '../components/Link';
import { PageBack } from '../components/PageBack';
import { PageHome } from '../components/PageHome';
import { HandoffButton } from '../components/HandoffButton';
import { ActionArrow } from '../components/ActionArrow';
import { EmberMediaHeader } from '../components/EmberMediaHeader';
import { EmberWritingProse } from '../components/EmberWritingProse';
import { InboundRegistrationCTA } from '../components/InboundRegistrationCTA';
import EmberFlame from '../../../../../shared-web/EmberFlame';
import { PassFailureNotice } from '../components/PassFailureNotice';
import { BackgroundRow } from '../components/BackgroundRow';
import { ResultCeremony } from '../tree/ResultCeremony';
import { NodeSeals } from '../tree/NodeSeals';
import type { TreeNode } from '../api';
import './Result.css';
import { PageFailure } from '../components/PageFailure';
import { PageLoading } from '../components/PageLoading';


type DraftCharacter = { field: 'title' | 'text'; value: string };

const graphemes = (value: string): string[] => {
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const Segmenter = Intl.Segmenter as unknown as new (
      locale?: string,
      options?: { granularity: 'grapheme' },
    ) => { segment(input: string): Iterable<{ segment: string }> };
    return [...new Segmenter('en', { granularity: 'grapheme' }).segment(value)]
      .map((item) => item.segment);
  }
  return Array.from(value);
};

const EMPTY_DRAFT_MEDIA: EmberMedia = {
  header_image_url: '',
  music_url: '',
  header_image_pending: true,
  music_pending: true,
};

export interface ResultPageProps {
  /** The telling, by its conversation. */
  conversationId: string;
  /**
   * Legacy tab suffix from old '/result/:id/(ember|questions)' links. Both
   * render the same single page; 'questions' opens the questions dialog.
   */
  tab: 'ember' | 'questions' | null;
  hash: string;
  search: URLSearchParams;
}

/**
 * S07 — the Unified Post-conversation Result (v4.0 §4.7): one page, one job —
 * hand back what the person just said, as their own Ember.
 *
 *   E01 own Ember, closed by the ownership line (From here, it's yours.)
 *   E09 register — after the reward, never a gate
 *   E08 tell another
 *
 * v3.0's network route (E02–E07: entry outcome, direct deliveries, the sharing
 * decision, readable questions, publish my question, explore) is retired
 * from this surface (2026-09-04 拍板): telling is not sharing, and the only
 * way an Ember ever reaches another person is the teller's own yes (§4.9).
 * A visitor is a full owner here: there is no claim gate and no auth wall.
 */
export function ResultPage({ conversationId, tab, hash, search }: ResultPageProps): ReactElement {
  const openTalk = useTalkSheet();
  const session = useSession();
  const returnTo = sanitizeReturnTo(search.get('returnTo'), '');
  const resultPath = withReturnTo(`/result/${conversationId}`, returnTo);
  const [data, setData] = useState<ResultResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftText, setDraftText] = useState('');
  const [settling, setSettling] = useState(false);
  // §4.17: the telling that just ended arrives with ?writing=1. Only it gets
  // the ceremony over the page and the seals landing one by one; a revisit
  // shows the seals still.
  const justTold = search.get('writing') === '1';
  const [ceremony, setCeremony] = useState(justTold);
  const [nodesWaitOver, setNodesWaitOver] = useState(false);
  const draftTarget = useRef({ title: '', text: '' });
  const draftQueue = useRef<DraftCharacter[]>([]);
  const previousPhase = useRef<ResultResponse['phase'] | null>(null);
  const settleTimer = useRef<number | null>(null);

  const mediaPollsLeft = useRef(24);
  const streamErrorSent = useRef(false);
  const refreshFailedSent = useRef(false);
  const mediaGiveUpSent = useRef(false);
  const mediaPendingRef = useRef({ image: false, music: false });

  const queueDraft = useCallback((field: DraftCharacter['field'], next: string): void => {
    const current = draftTarget.current[field];
    if (next === current || current.startsWith(next)) return;
    if (!next.startsWith(current)) {
      draftTarget.current[field] = next;
      draftQueue.current = draftQueue.current.filter((item) => item.field !== field);
      if (field === 'title') setDraftTitle(next);
      else setDraftText(next);
      return;
    }
    const suffix = next.slice(current.length);
    draftTarget.current[field] = next;
    draftQueue.current.push(...graphemes(suffix).map((value) => ({ field, value })));
  }, []);

  const appendDraft = useCallback((field: DraftCharacter['field'], delta: string): void => {
    if (!delta) return;
    queueDraft(field, `${draftTarget.current[field]}${delta}`);
  }, [queueDraft]);

  // Provider chunks stay provider-paced, but the visible hand writes one
  // grapheme per frame. A long chunk never flashes onto the page whole.
  useEffect(() => {
    if (data?.phase !== 'finalizing') return undefined;
    let frame = 0;
    const writeNext = (): void => {
      const next = draftQueue.current.shift();
      if (next?.field === 'title') setDraftTitle((value) => value + next.value);
      if (next?.field === 'text') setDraftText((value) => value + next.value);
      frame = window.requestAnimationFrame(writeNext);
    };
    frame = window.requestAnimationFrame(writeNext);
    return () => window.cancelAnimationFrame(frame);
  }, [data?.phase]);

  /** Load/refresh. */
  const applyData = useCallback((fresh: ResultResponse): void => {
    if (fresh.draft) {
      queueDraft('title', fresh.draft.title || '');
      queueDraft('text', fresh.draft.canonical_text || '');
    }
    const arrivedFromWriting = previousPhase.current === 'finalizing'
      || (previousPhase.current === null && search.get('writing') === '1');
    if (fresh.phase === 'finalized' && fresh.ember && arrivedFromWriting) {
      draftQueue.current = [];
      draftTarget.current = {
        title: fresh.ember.title || '',
        text: fresh.ember.canonical_text || '',
      };
      setDraftTitle(fresh.ember.title || '');
      setDraftText(fresh.ember.canonical_text || '');
      setSettling(true);
      if (settleTimer.current !== null) window.clearTimeout(settleTimer.current);
      settleTimer.current = window.setTimeout(() => setSettling(false), 900);
      if (search.get('writing') === '1') replaceUrl(resultPath);
    }
    previousPhase.current = fresh.phase;
    setData(fresh);
  }, [queueDraft, resultPath, search]);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      applyData(await emberApi.getResult(conversationId));
    } catch {
      if (!refreshFailedSent.current) {
        refreshFailedSent.current = true;
        trackFailed('result_load', 'refresh_failed', { conversation_id: conversationId });
      }
    }
  }, [conversationId, applyData]);

  // Initial load (and full reset when the conversation changes).
  useEffect(() => {
    let alive = true;
    setData(null);
    setFailed(false);
    setDraftTitle('');
    setDraftText('');
    setSettling(false);
    draftTarget.current = { title: '', text: '' };
    draftQueue.current = [];
    previousPhase.current = null;
    if (settleTimer.current !== null) {
      window.clearTimeout(settleTimer.current);
      settleTimer.current = null;
    }
    mediaPollsLeft.current = 24;
    streamErrorSent.current = false;
    refreshFailedSent.current = false;
    mediaGiveUpSent.current = false;
    if (!conversationId) return undefined;
    emberApi.getResult(conversationId)
      .then((d) => {
        if (!alive) return;
        applyData(d);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        const status = (err as { status?: number }).status;
        if (status === 404 || status === 401 || status === 403) {
          // Not this browser's telling — or not a row any more: since the fold
          // (migration 242, 2026-09-16) a registered person's history lives in
          // their one conversation and the /result rows it came from are gone,
          // so a link from their own history met a dead page (prod 09-17/18,
          // five people). Everything they told is on the Talk page now, which
          // opens their own conversation by itself — and a stranger's fresh
          // one, as it always did (2026-09-19 拍板). The event stays: it is
          // how these arrivals are counted.
          trackFailed('result_load', 'not_found', { conversation_id: conversationId });
          navigate('/talk', { replace: true });
        } else {
          setFailed(true);
          trackFailed('result_load', 'load_failed', { conversation_id: conversationId });
        }
      });
    return () => {
      alive = false;
      if (settleTimer.current !== null) window.clearTimeout(settleTimer.current);
    };
  }, [conversationId, applyData]);

  const writing = data?.phase === 'finalizing';
  const ember = data?.ember ?? null;
  const nodes = data?.nodes ?? [];
  const nodesStatus: 'pending' | 'done' = data?.nodes_status ?? 'done';
  // The pass runs behind the Result; keep listening for its frame a while,
  // then stop — a light that never lands is not a broken page.
  useEffect(() => {
    if (!justTold) return undefined;
    const t = window.setTimeout(() => setNodesWaitOver(true), 75_000);
    return () => window.clearTimeout(t);
  }, [justTold]);
  const mediaUnresolved = !!ember
    && (ember.media?.header_image_pending === true || ember.media?.music_pending === true);
  mediaPendingRef.current = {
    image: !!ember && ember.media?.header_image_pending === true,
    music: !!ember && ember.media?.music_pending === true,
  };
  const nodesUnresolved = justTold && !!ember && nodesStatus === 'pending' && !nodesWaitOver;
  const progressActive = writing || mediaUnresolved || nodesUnresolved;

  // Since 2026-10-21 (phase 3) the progress rides the conversation's channel (live.ts `progress`): on joining, the
  // draft so far is read once (progress-snapshot), then every event as the hub publishes it — no stream of its own
  useEffect(() => {
    if (!progressActive) return undefined;
    let closed = false;
    const handle = (event: Record<string, unknown>): void => {
      if (closed) return;
      const type = String(event.type || '');
      if (type === 'snapshot') {
        const snapshot = event.snapshot as {
          title?: string;
          segments?: string[];
          media?: {
            image_status?: string;
            header_image_url?: string;
            music_status?: string;
            music_url?: string;
          };
        } | undefined;
        queueDraft('title', snapshot?.title || '');
        queueDraft('text', Array.isArray(snapshot?.segments) ? snapshot.segments.join('\n\n') : '');
        setData((current) => {
          if (!current) return current;
          const currentMedia = current.draft?.media || current.ember?.media || EMPTY_DRAFT_MEDIA;
          const headerImageUrl = snapshot?.media?.header_image_url || currentMedia.header_image_url || '';
          const musicUrl = snapshot?.media?.music_url || currentMedia.music_url || '';
          const media = {
            header_image_url: headerImageUrl,
            music_url: musicUrl,
            header_image_pending: !headerImageUrl && snapshot?.media?.image_status === 'preparing',
            music_pending: !musicUrl && snapshot?.media?.music_status === 'preparing',
          };
          if (current.draft) return { ...current, draft: { ...current.draft, media } };
          if (current.ember) return { ...current, ember: { ...current.ember, media } };
          return current;
        });
        return;
      }
      if (type === 'title_delta') appendDraft('title', String(event.delta || ''));
      if (type === 'segment_start' && Number(event.index) > 0
        && draftTarget.current.text && !draftTarget.current.text.endsWith('\n\n')) {
        appendDraft('text', '\n\n');
      }
      if (type === 'text_delta') appendDraft('text', String(event.delta || ''));
      if (type === 'media_briefs') {
        setData((current) => {
          if (!current) return current;
          const media = { ...(current.draft?.media || current.ember?.media || EMPTY_DRAFT_MEDIA) };
          media.header_image_pending = !media.header_image_url && event.has_image === true;
          media.music_pending = !media.music_url && event.has_music === true;
          if (current.draft) return { ...current, draft: { ...current.draft, media } };
          if (current.ember) return { ...current, ember: { ...current.ember, media } };
          return current;
        });
      }
      if (type === 'media_ready') {
        setData((current) => {
          if (!current) return current;
          const media = { ...(current.draft?.media || current.ember?.media || EMPTY_DRAFT_MEDIA) };
          if (event.kind === 'image') {
            media.header_image_url = String(event.url || '');
            media.header_image_pending = false;
          }
          if (event.kind === 'audio') {
            media.music_url = String(event.url || '');
            media.music_pending = false;
          }
          if (current.draft) return { ...current, draft: { ...current.draft, media } };
          if (current.ember) return { ...current, ember: { ...current.ember, media } };
          return current;
        });
      }
      if (type === 'finalized') void refresh();
      if (type === 'nodes') {
        // §4.17: where this telling's lights landed.
        const landed = Array.isArray(event.nodes) ? (event.nodes as TreeNode[]) : [];
        setData((current) => (current ? { ...current, nodes: landed, nodes_status: 'done' } : current));
      }
      if (['needs_more_context', 'qa_rejected', 'story_failed', 'failed'].includes(type)) {
        closed = true;
        void refresh();
      }
    };
    const sub = subscribeConversation(conversationId, {
      onEvent: (ev) => { if (Array.isArray(ev?.what) && ev.what.includes('progress')) handle(ev as unknown as Record<string, unknown>); },
      onSubscribed: () => {
        // The state too (2026-10-22 release check): a writing that ended before the page joined sends no
        // `finalized` any more, and the snapshot answers 204 once it is over — the page stood on "writing".
        void refresh();
        emberApi.resultProgressSnapshot(conversationId)
          .then((r) => { if (r?.snapshot) handle({ type: 'snapshot', snapshot: r.snapshot }); })
          .catch(() => { /* the events still come; the durable read covers the rest */ });
      },
      onError: (why) => {
        if (streamErrorSent.current) return;
        streamErrorSent.current = true;
        trackFailed('result_load', 'stream_error', { conversation_id: conversationId, error_detail: why });
      },
    });
    return () => { closed = true; sub.close(); };
  }, [progressActive, conversationId, appendDraft, queueDraft, refresh]);

  // The durable state (the Ember written, its picture or music landed) is told on the person's own channel
  // (live.ts `ember`) and read again then, and on each return to the tab — no clock (phase 3; until then every
  // second for a minute, then every five, and a 5-second media clock for two minutes)
  useEffect(() => {
    if (!writing && !mediaUnresolved) return undefined;
    const again = (): void => { void refresh(); };
    const onVisible = (): void => { if (document.visibilityState === 'visible') again(); };
    window.addEventListener(LIVE_EMBER_EVENT, again);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(LIVE_EMBER_EVENT, again);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [writing, mediaUnresolved, refresh]);



  const emberError = data?.ember_error ?? null;
  const doneState = data?.phase === 'finalized' && !ember && !emberError;
  // Canonicalize legacy /result/:id/(ember|questions) links and module
  // anchors onto the bare URL (§2.1): the v3.0 modules they pointed at are
  // not on this page any more.
  useEffect(() => {
    if (!data || doneState || (tab === null && !hash)) return;
    navigate(resultPath, { replace: true });
  }, [data, doneState, tab, hash, resultPath]);

  const registered = session.status === 'authenticated';

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  if (failed) {
    return <PageFailure title="Couldn't load your result" detail="Refresh to try again." logState="load_failed" />;
  }
  // §4.17 the ceremony: the tree over the whole screen for a few seconds
  // — the teller speaks, a light leaves their head and lands lit in the
  // canopy — then the page unrolls from the top. It runs from the first frame
  // of this page, over whatever the page has at that moment (the draft still
  // being written, or nothing yet); the page never waits for the ceremony,
  // and the ceremony waits for the page only at the very end, for a title.
  // It is always the first child of the same fragment, whatever the page
  // under it is that frame: a different position would remount it, and its
  // clock would start over.
  const overlay = ceremony
    ? <ResultCeremony ready={data !== null && (!writing || draftTitle.length > 0)} onDone={() => setCeremony(false)} />
    : null;
  if (!data) {
    if (search.get('writing') === '1') {
      return (
        <>
          {overlay}
          <ResultWritingPage
            returnTo={returnTo}
            title={draftTitle}
            text={draftText}
            media={EMPTY_DRAFT_MEDIA}
            logConversationId={conversationId}
          />
        </>
      );
    }
    return <PageLoading label="Loading your result…" />;
  }

  if (data.phase === 'failed') {
    return (
      <ResultTerminalState
        returnTo={returnTo}
        title="We couldn't finish your Ember."
        detail="Your telling is still safe. Keep talking, and Ember will try again once you pause."
        actionLabel="Keep telling"
        logState="failed"
        logConversationId={conversationId}
        onAction={() => navigate(withReturnTo(
          `/talk?mode=chat&conversation_id=${encodeURIComponent(conversationId)}`,
          returnTo,
        ), { replace: true })}
      />
    );
  }

  if (data.phase === 'needs_more_context') {
    const missing = (data.missing_information || [])[0];
    return (
      <ResultTerminalState
        returnTo={returnTo}
        title="This telling needs a little more."
        detail={missing ? `Still missing: ${missing}` : 'Add what happened and what it meant to you, and Ember will write it once you pause.'}
        actionLabel="Keep telling"
        logState="needs_more_context"
        logConversationId={conversationId}
        onAction={() => navigate(withReturnTo(
          `/talk?mode=chat&conversation_id=${encodeURIComponent(conversationId)}`,
          returnTo,
        ), { replace: true })}
      />
    );
  }

  if (doneState) {
    return (
      <div className="ember-container result-state-page">
        <ResultToolbar returnTo={returnTo} />
        <div className="ember-state-card result-done">
          <span className="ember-state-icon" aria-hidden>
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </span>
          <h1 className="result-done-title">This telling is complete.</h1>
          <p className="result-done-text">
            Nothing was lost. Come back whenever another real experience comes
            to mind.
          </p>
          <Link to={returnTo || '/'} className="ember-btn ember-btn-primary">
            {returnTo ? `Back to ${returnToLabel(returnTo, 'Home')}` : 'Home'}
          </Link>
        </div>
      </div>
    );
  }

  const registerSection = !registered && (data.phase === 'finalized')
    ? <KeepEmberSection returnTo={resultPath} logConversationId={conversationId} />
    : null;

  const tellAnotherSection = (
    <section className="result-section result-tell-another" aria-label="Tell another">
      <button
        type="button"
        className="ember-text-action result-text-action"
        data-log="result_tell_another"
        data-log-conversation-id={conversationId}
        onClick={() => openTalk({ entryType: 'home', returnTo: resultPath })}
      >
        Tell another experience <ActionArrow />
      </button>
    </section>
  );

  return (
    <>
      {overlay}
      <div className={`ember-container ember-reading result-page${writing ? ' result-page-writing' : ''}`}>
        <div className="result-toolbar">
          <ResultBackLink returnTo={returnTo} />
          <div className="ember-toolbar-actions result-toolbar-actions">
            {/* Cold arrivals from an SMS result link have no parent list Back can
                reach, so Result carries the same global exit as the other
                toolbar-row detail pages. */}
            <PageHome />
            {/* §4.18: send this Ember to someone — once it exists. */}
            {ember && data.phase === 'finalized' && (
              <HandoffButton
                subject={{ kind: 'ember', id: ember.id }}
                title={ember.title || 'An Ember'}
                handoffPath={ember.handoff_path}
                onHandoffPath={(handoffPath) => setData((current) => (current?.ember
                  ? { ...current, ember: { ...current.ember, handoff_path: handoffPath } }
                  : current))}
              />
            )}
          </div>
        </div>
        {!ember && emberError && (
          <PassFailureNotice
            title="We couldn't write your Ember from this conversation."
            reason={emberError}
            logState="story"
            logConversationId={conversationId}
          >
            Your words are saved and nothing was shared. Tell it again whenever
            you're ready.
            <div>
              <button
                type="button"
                data-log="talk_open"
                data-log-discovery="result_failed"
                onClick={() => openTalk({ entryType: 'home', returnTo: resultPath })}
                className="ember-btn ember-btn-primary"
              >
                Tell it again
              </button>
            </div>
          </PassFailureNotice>
        )}

        {!ember && data.phase === 'qa_rejected' && (
          <PassFailureNotice
            title="This draft couldn't become an Ember."
            reason="It did not pass final review."
            logState="qa"
            logConversationId={conversationId}
          >
            It did not pass the grounding, privacy, language, and lived-experience
            checks that every Ember must pass. The draft was not saved or shared.
            <div>
              <button
                type="button"
                data-log="talk_open"
                data-log-discovery="result_done"
                onClick={() => openTalk({ entryType: 'home', returnTo: resultPath })}
                className="ember-btn ember-btn-primary"
              >
                Tell another
              </button>
            </div>
          </PassFailureNotice>
        )}

        {/* E01 — the own Ember always leads (§4.7). The media header lives
            OUTSIDE the prose blocks, in a slot occupied through the whole
            finalizing→finalized flip: a remount would destroy the <audio> the
            reader is listening to. */}
        {(writing || ember) && (
          <EmberMediaHeader
            media={writing
              ? (data.draft?.media || EMPTY_DRAFT_MEDIA)
              : (ember?.media || { header_image_url: '', music_url: '' })}
            alt={`${writing ? (draftTitle || 'Ember draft') : (ember?.title || 'Untitled Ember')} — illustration`}
            draft={writing}
            logEmberId={ember?.id}
            logConversationId={conversationId}
          />
        )}
        {writing && (
          <EmberWritingProse
            title={draftTitle}
            text={draftText}
            draft
            logState="draft"
            logConversationId={conversationId}
          />
        )}
        {ember && (
          <>
            <EmberWritingProse
              title={settling ? draftTitle : (ember.title || 'Untitled Ember')}
              text={settling ? draftText : ember.canonical_text}
              draft={false}
              settling={settling}
              logState="final"
              logConversationId={conversationId}
              underTitle={(
                <NodeSeals
                  nodes={nodes}
                  status={nodesStatus}
                  animate={justTold}
                  emberId={ember.id}
                  conversationId={conversationId}
                />
              )}
            >
              {/* The colophon is the whole close (2026-09-05): what this is and
                  whose. The first screen has already said where it goes from
                  here; saying it a third time under the piece read as a notice. */}
              <p className="result-meta result-meta-definition">Made from what you just told. Yours to keep.</p>
            </EmberWritingProse>

            {ember.status === 'published' && ember.impact.resonance_count > 0 && registered && (
              <section className="result-section" aria-label="Who this mattered to">
                <div className="result-section-head"><h2>Who this mattered to</h2></div>
                <Link
                  to={`/me/embers/${ember.id}`}
                  className="result-impact-link"
                  data-log="result_see_who"
                  data-log-ember-id={ember.id}
                  data-log-conversation-id={conversationId}
                >
                  <span className="result-impact-count">
                    Mattered to {plural(ember.impact.resonance_count, 'person', 'people')}
                    {ember.impact.unread_count > 0 && (
                      <span className="result-impact-new">{ember.impact.unread_count} new</span>
                    )}
                  </span>
                  <span className="result-impact-open">See who <span aria-hidden>→</span></span>
                </Link>
                <p className="result-impact-note">Private to you. Nobody else sees this.</p>
              </section>
            )}
          </>
        )}

        {/* Background experience (2026-09-12 data contract): what this Ember
            may do from here, as a state line — no question asked here. */}
        {!writing && ember && <BackgroundRow emberId={ember.id} />}

        {/* E09 right after the E01 reward (2026-09-01 拍板, was after the route):
            at the old page-bottom position only half the finalized visits ever
            scrolled to it. It stays outside the route and carries no lamp. */}
        {!writing && registerSection}
        {!writing && tellAnotherSection}
      </div>
    </>
  );
}

function ResultWritingPage({ returnTo, title, text, media, logConversationId }: {
  returnTo: string;
  title: string;
  text: string;
  media: EmberMedia;
  logConversationId?: string;
}): ReactElement {
  return (
    <div className="ember-container ember-reading result-page result-page-writing">
      <ResultToolbar returnTo={returnTo} />
      <EmberMediaHeader
        media={media}
        alt={`${title || 'Ember draft'} — illustration`}
        draft
        logConversationId={logConversationId}
      />
      <EmberWritingProse
        title={title}
        text={text}
        draft
        logState="draft"
        logConversationId={logConversationId}
      />
    </div>
  );
}

function ResultTerminalState({ returnTo, title, detail, actionLabel, onAction, logState, logConversationId }: {
  returnTo: string;
  title: string;
  detail: string;
  actionLabel: string;
  onAction: () => void;
  /** Which terminal phase the keep-telling tap left from; the click is logged only when set. */
  logState?: string;
  logConversationId?: string;
}): ReactElement {
  const registered = useSession().status === 'authenticated';
  return (
    <div className="ember-container result-state-page">
      <ResultToolbar returnTo={returnTo} />
      <div className="ember-state-card result-done">
        <h1 className="result-done-title">{title}</h1>
        <p className="result-done-text">{detail}</p>
        <button
          type="button"
          className="ember-btn ember-btn-primary"
          data-log={logState ? 'result_keep_telling' : undefined}
          data-log-state={logState}
          data-log-conversation-id={logState ? logConversationId : undefined}
          onClick={onAction}
        >
          {actionLabel}
        </button>
      </div>
      {/* E09 on the non-finalized endings too (2026-09-01 拍板): a guest whose
          telling didn't finish otherwise leaves with no way back to it — the
          unfinished telling lives in this browser's cookie and is continuable
          from My Embers once the visitor merges into an account. */}
      {!registered && (
        <KeepEmberSection returnTo={returnTo} unfinished logState={logState} logConversationId={logConversationId} />
      )}
    </div>
  );
}

/* E09 (§4.6.2), redrawn 2026-09-06 as the Ember's last lines rather than a
   card: a hairline after the colophon, the serif title with the amber flame,
   one sentence, then the landing closer's own door and note — so every
   registration door on the site is one face (`Join Ember` / `One text. No
   password.`). The grey utility card it replaces was the one saturated block
   on the paper, and read as a form after the reading. */
function KeepEmberSection({ returnTo, unfinished = false, logState, logConversationId }: {
  returnTo: string;
  /** The telling never finalized: there is no Ember yet, only this browser's thread. */
  unfinished?: boolean;
  logState?: string;
  logConversationId?: string;
}): ReactElement {
  // How many of this browser's Embers the one text would carry (2026-09-06
  // 拍板): a visitor with several hears the count, so the door says what it
  // keeps. Unregistered only — this section never renders signed in.
  const [count, setCount] = useState(1);
  useEffect(() => {
    let alive = true;
    emberApi.treeYou().then((r) => { if (alive && r.your_ember && r.your_ember.count > 1) setCount(r.your_ember.count); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const several = !unfinished && count > 1;
  return (
    <section
      className="result-section result-keep"
      aria-labelledby="result-keep-title"
      data-log-seen="result_register"
      data-log-state={logState}
      data-log-conversation-id={logConversationId}
    >
      <h2 id="result-keep-title" className="result-keep-title">
        <EmberFlame className="result-keep-flame" />
        {unfinished ? 'Keep this telling with you.' : several ? 'Keep your Embers with you.' : 'Keep this Ember with you.'}
      </h2>
      <p className="result-keep-text">
        {unfinished
          ? 'Right now it lives only in this browser. One text and it’s yours on every device, ready to pick up again.'
          : several
            ? `Right now your ${count} Embers live only in this browser. One text and they’re yours on every device — and Ember can reach you when it has something to say.`
            : 'Right now it lives only in this browser. One text and it’s yours on every device — and Ember can reach you when it has something to say.'}
      </p>
      <div className="result-keep-foot">
        <InboundRegistrationCTA
          className="result-keep-cta"
          label="Join Ember"
          returnTo={returnTo}
          logName="result_register_open"
        />
        <span className="result-keep-note">One text. No password.</span>
      </div>
    </section>
  );
}

function ResultBackLink({ returnTo, onIntercept }: {
  returnTo: string;
  /** Return true to hold the trip (the questions dialog takes this click). */
  onIntercept?: () => boolean;
}): ReactElement {
  return (
    <PageBack returnTo={returnTo} className="result-home-link" onIntercept={onIntercept} />
  );
}

/**
 * The Back + Home row every result sub-state carries (writing, terminal
 * states): all of them sit at the end of a telling, where Back can only
 * retrace the flow.
 */
function ResultToolbar({ returnTo }: { returnTo: string }): ReactElement {
  return (
    <div className="result-toolbar">
      <ResultBackLink returnTo={returnTo} />
      <div className="ember-toolbar-actions result-toolbar-actions">
        <PageHome />
      </div>
    </div>
  );
}
